// "€/m² di zona" e storico dei prezzi (backend/prezzo_zona.py e backend/ricontrolli.py): testi per la scheda
// dell'immobile (cliente e staff) e per il parere (solo staff: lo scostamento).

import { contesto, dataIn, PRINCIPALE } from '../i18n';
import { migliaia, formatEuro, leggiDataUtcRoma, ilGiorno } from './percorso';

const MESI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];

export const euro = (n) => (n || n === 0 ? `${migliaia(n)} €` : '');
export const euroMq = (n) => (n ? `${migliaia(n)} €/m²` : '');

const meseAnno = (iso) => {
  const d = iso ? leggiDataUtcRoma(iso) : null; // giorno e mese di Roma, anche con il telefono in un altro fuso
  return d && !Number.isNaN(d.getTime()) ? `${MESI[d.getMonth()]} ${d.getFullYear()}` : '';
};
// "8 set" ("8 сент." in russo): il giorno è già quello di Roma, quindi niente fuso in Intl (come utils/percorso)
const giornoMese = (iso, lingua = PRINCIPALE) => {
  const d = iso ? leggiDataUtcRoma(iso) : null; // giorno e mese di Roma, anche con il telefono in un altro fuso
  if (!d || Number.isNaN(d.getTime())) return '';
  return lingua === PRINCIPALE ? `${d.getDate()} ${MESI[d.getMonth()]}`
    : dataIn(lingua, d, { day: 'numeric', month: 'short', timeZone: undefined });
};

// "2.450 €/m² · media di 8 case entro 1 km (mar 2026 – set 2026)"
export const testoZona = (zona) => {
  if (!zona || !zona.media) return '';
  const periodo = zona.dal && zona.al ? (meseAnno(zona.dal) === meseAnno(zona.al) ? meseAnno(zona.al) : `${meseAnno(zona.dal)} – ${meseAnno(zona.al)}`) : '';
  return `${euroMq(zona.media)} · media di ${zona.n} case entro ${String(zona.raggio_km || 1).replace('.', ',')} km${periodo ? ` (${periodo})` : ''}`;
};

// "OMI zona B1 centrale: 2.400–2.900 €/m² (abitazioni civili, 2025/2)"
export const testoOmi = (omi) => {
  if (!omi || !omi.min) return '';
  const dove = omi.livello === 'zona' ? `zona ${omi.zona}${omi.fascia ? ` ${omi.fascia}` : ''}` : 'comune';
  const forbice = omi.max && omi.max !== omi.min ? `${migliaia(omi.min)}–${euroMq(omi.max)}` : euroMq(omi.min);
  const tipo = (omi.tipologia || '').toLowerCase();
  return `OMI ${dove}: ${forbice}${tipo || omi.semestre ? ` (${[tipo, omi.semestre].filter(Boolean).join(', ')})` : ''}`;
};

// "+12% sopra la media di zona" / "8% sotto la media di zona" / "in linea con la media di zona"
export const testoScostamento = (n, di = 'la media di zona') => {
  if (n === null || n === undefined) return '';
  if (Math.abs(n) < 3) return `in linea con ${di}`;
  return n > 0 ? `+${n}% sopra ${di}` : `${Math.abs(n)}% sotto ${di}`;
};

// Le variazioni di prezzo, dalla più recente: "Sceso da 389.000 € a 369.000 € · 12 set" (la scheda del cliente la
// legge nella sua lingua: cifre, mese e frase; le pagine dello staff restano in italiano)
export const variazioniPrezzo = (storico, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const cifra = (n) => (lingua === PRINCIPALE ? euro(n) : formatEuro(n, lingua) || '');
  return [...(storico || [])]
    .filter((v) => v && v.a)
    .sort((x, y) => String(y.data).localeCompare(String(x.data)))
    .map((v) => {
      const giu = v.da ? v.a < v.da : false;
      const chiave = v.da ? (giu ? 'immobili.scheda.prezzo_sceso' : 'immobili.scheda.prezzo_salito') : 'immobili.scheda.prezzo_primo';
      const quando = giornoMese(v.data, lingua);
      return { giu, testo: `${t(chiave, { da: cifra(v.da), a: cifra(v.a) })}${quando ? ` · ${quando}` : ''}` };
    });
};

export const ritirato = (immobile) => immobile?.annuncio_stato === 'ritirato';
export const testoRitirato = (immobile) => `Annuncio ritirato dal portale${immobile?.data_ritiro ? ` ${ilGiorno(giornoMese(immobile.data_ritiro))}` : ''} · chiedi all'agenzia se è venduto`;

// ---------------------------------------------------------------- casa venduta o affittata
//
// Gemelli di `ritirato`/`testoRitirato`, e la differenza fra i due conta: **ritirato non vuol dire venduto**.
// Da un annuncio tolto non sappiamo se la casa è stata venduta o solo levata dal portale, e lì si scrive
// «chiedi all'agenzia». Qui invece il portale lo dice nei suoi dati (backend/stato_annuncio.py), quindi si
// scrive la cosa, con la data.
const CHIUSI = ['venduto', 'affittato'];
const ETICHETTE = { venduto: 'Venduto', affittato: 'Affittato' };

export const venduto = (immobile) => CHIUSI.includes(immobile?.venduto?.stato);

/** «Venduto il 12/09/2026», o «Venduto · rilevato il …» quando la data della vendita non la sappiamo.
 *
 * Il testo lo compone il server quando segna la vendita, e si mostra quello (backend/stato_annuncio.py,
 * `testo_chiuso`): così la scheda dello staff, la lista del cliente e la scheda dell'agenzia dicono per forza
 * la stessa cosa. Riformattarlo qui vorrebbe dire due posti da tenere d'accordo — e il formato corto di questo
 * file («12 set») perde l'anno, che su una data di vendita è l'informazione che serve.
 *
 * La riserva qui sotto serve alle case segnate prima che il testo si salvasse, e alle date nel futuro che il
 * portale a volte scrive (via Morandi 7 diceva «venduto il 23/12/2026» il 03/10, probabilmente la data
 * dell'atto ancora da firmare). */
export const testoVenduto = (immobile) => {
  const v = immobile?.venduto;
  if (!venduto(immobile)) return '';
  if (v.testo) return v.testo;
  const etichetta = ETICHETTE[v.stato];
  const quando = v.data ? ilGiorno(giornoMese(v.data)) : '';
  if (v.data_nel_futuro && v.data_dal_portale) {
    return `${etichetta} · rilevato ${quando} (il portale indica ${ilGiorno(giornoMese(v.data_dal_portale))})`;
  }
  if (v.data_rilevata) return `${etichetta} · rilevato ${quando}`;
  return `${etichetta} ${quando}`;
};

/** L'etichetta corta per una lista: «Venduto», «Affittato». */
export const etichettaVenduto = (immobile) => (venduto(immobile) ? ETICHETTE[immobile.venduto.stato] : '');
